import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const DEFAULT_FREE_TRIAL_STATUS = {
  isEligible: true,
  remainingPhotos: 10,
  photosUsed: 0,
  maxPhotos: 10,
  hasUsedFreeTrial: false
};

const useFreeTrialManagement = () => {
  const [freeTrialStatus, setFreeTrialStatus] = useState(null);
  const [loadingFreeTrial, setLoadingFreeTrial] = useState(true);

  const ensureFreeTrialStatus = useCallback(() => {
    setLoadingFreeTrial(false);
    setFreeTrialStatus((current) => current || DEFAULT_FREE_TRIAL_STATUS);
  }, []);

  const requestFreeTrialStatus = useCallback(
    async ({ showLoading = true } = {}) => {
      if (showLoading) {
        setLoadingFreeTrial(true);
      }

      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        );

        const response = await Promise.race([
          axios.get('/api/users/free-trial-status', { withCredentials: true }),
          timeoutPromise
        ]);

        setFreeTrialStatus(response.data);
      } catch (error) {
        console.error('Failed to refresh free trial status:', error);
        ensureFreeTrialStatus();
      } finally {
        if (showLoading) {
          setLoadingFreeTrial(false);
        }
      }
    },
    [ensureFreeTrialStatus]
  );

  useEffect(() => {
    requestFreeTrialStatus();
  }, [requestFreeTrialStatus]);

  useEffect(() => {
    const handleFocus = () => {
      requestFreeTrialStatus({ showLoading: false });
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [requestFreeTrialStatus]);

  return {
    freeTrialStatus,
    loadingFreeTrial,
    refreshFreeTrialStatus: requestFreeTrialStatus,
    ensureFreeTrialStatus
  };
};

export { DEFAULT_FREE_TRIAL_STATUS };
export default useFreeTrialManagement;
